import { Theme, useTheme } from '@pmi.web/react-theme';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { PropsWithChildren, useMemo } from 'react';

import { PadlockIcon } from '@/components/common/icons';
import { FitlineLogo, PMLogo } from '@/components/common/logos';
import { PillType } from '@/components/common/pills/enums';
import { FilledPill } from '@/components/common/pills/FilledPill';
import { Paragraph } from '@/components/common/text';

import { Footer } from './Footer';
import { UserDetails } from './UserDetails';

const styles = {
  page: 'flex flex-col mt-0 max-w-panel mx-auto xs:rounded-panel',
  pageHeader:
    'flex justify-between h-[65px] md:h-[72px] px-sm py-xs md:px-md md:py-xs 2xl:px-md 2xl:py-xs bg-white items-center',
  pageHeaderDetails:
    'flex flex-col justify-between text-right text-sm break-all',
};

interface PageLayoutProps extends PropsWithChildren {
  readonly headerDescription?: string;
}

export function PageLayout({ headerDescription, children }: PageLayoutProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isPaymentsPreviewUser } = useAuth();

  const logo = useMemo(() => {
    if (theme === Theme.FitLine) {
      return <FitlineLogo />;
    }

    if (theme === Theme.PM) {
      return <PMLogo />;
    }

    return null;
  }, [theme]);

  return (
    <>
      <header className={styles.pageHeader}>
        {logo}
        <div className={styles.pageHeaderDetails}>
          <div className="flex justify-end items-center space-x-sm">
            <FilledPill
              type={PillType.Success}
              icon={<PadlockIcon />}
              text={t('Secure payment')}
              extraClassName="text-xs font-bold md:text-sm bg-[#ebf2ef] text-[#397f5c] font-heading font-medium leading-[100%] tracking-[2%]"
            />
            <UserDetails />
            {headerDescription && (
              <span className="ml-sm md:ml-md 2xl:ml-lg text-silver">
                <Paragraph>{headerDescription}</Paragraph>
              </span>
            )}
          </div>
        </div>
      </header>
      <div className={styles.page}>
        {isPaymentsPreviewUser && (
          <div className="bg-error animate-pulse text-center text-white p-sm">
            Preview mode
          </div>
        )}
        <main>{children}</main>
      </div>
      <Footer />
    </>
  );
}
